import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Checkbox,
  ListItemText,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CreditCard as CreditCardIcon,
  MonetizationOn as MonetizationOnIcon,
  PaymentOutlined as PayPalIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { fetchVehicleById, fetchVehicles, createVehicle, updateVehicle } from '../../redux/slices/vehiclesSlice';
import { VEHICLE_TYPE_OPTIONS, SPECIAL_CAPABILITIES, migrateSpecialVehicleType } from '../../config/vehicleTypes';
import useVehicleQuotaStatus from '../../hooks/useVehicleQuotaStatus';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import LicensePlateInput, { isValidGermanPlate, normalizePlateForCompare } from '../common/LicensePlateInput';
import EntityDocumentsSection from '../common/EntityDocumentsSection';
import { DUE_MAINTENANCE_TYPES } from '../../config/vehicleMaintenance';
import { getVehicleDueDate } from '../../utils/vehicleDeadlines';
import { uploadPendingEntityDocuments } from '../../services/entityDocumentsService';

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const VehicleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isEditMode = !!id;
  const fromPath = location.state?.from;
  
  const { currentVehicle, vehicles, isLoading: vehicleLoading, error: vehicleError } = useSelector(state => state.vehicles);

  // Reservefahrzeug: wird inaktiv angelegt und belegt keinen Kontingentplatz.
  // Ist das Kontingent ausgeschöpft, geht Anlegen nur noch als Reserve.
  const quotaStatus = useVehicleQuotaStatus();
  const reserveForced = !isEditMode && quotaStatus.isAtLimit;
  const [createAsReserve, setCreateAsReserve] = useState(false);
  const effectiveReserve = reserveForced || createAsReserve;

  const [formData, setFormData] = useState({
    vehicleNumber: '',
    licensePlate: '',
    model: '',
    phoneNumber: '',
    ordnungsnummer: '',
    vehicleType: 1,
    specialCapabilities: [],
    taximeterCoupling: 'none',
    paymentMethods: 1, // Standardmäßig nur Barzahlung
    status: 'available', // Standardmäßig verfügbar
    maintenanceDue: {},
    documents: []
  });

  const [formErrors, setFormErrors] = useState({
    vehicleNumber: '',
    licensePlate: '',
    model: '',
    vehicleType: '',
    paymentMethods: ''
  });
  
  // Neuanlage: Dateien werden vorgemerkt und nach dem Anlegen hochgeladen,
  // weil der Storage-Pfad die Fahrzeug-ID braucht.
  const [pendingDocumentFiles, setPendingDocumentFiles] = useState([]);
  const [savingDocuments, setSavingDocuments] = useState(false);
  const documentUploadError = location.state?.documentUploadError || '';
  
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchVehicleById(id));
    }
  }, [dispatch, id, isEditMode]);

  // Gesamte Flotte für den Kennzeichen-Duplikat-Check laden. Duplikate haben
  // in Produktion dazu geführt, dass die kennzeichenbasierten App-Updates das
  // Fahrzeug eines anderen Fahrers übernommen haben — Eindeutigkeit wird
  // deshalb hier an der Quelle erzwungen.
  useEffect(() => {
    dispatch(fetchVehicles());
  }, [dispatch]);

  // Fahrzeug mit identischem (normalisiertem) Kennzeichen — eigenes Dokument
  // im Edit-Modus ausgenommen.
  const duplicatePlateVehicle = React.useMemo(() => {
    const plate = normalizePlateForCompare(formData.licensePlate);
    if (!plate || !isValidGermanPlate(formData.licensePlate)) return null;
    return (vehicles || []).find(v => (
      v.id !== id && normalizePlateForCompare(v.licensePlate) === plate
    )) || null;
  }, [vehicles, formData.licensePlate, id]);

  const duplicatePlateMessage = duplicatePlateVehicle
    ? `Dieses Kennzeichen existiert bereits (Fahrzeug ${duplicatePlateVehicle.vehicleNumber ? `Nr. ${duplicatePlateVehicle.vehicleNumber}` : duplicatePlateVehicle.model || duplicatePlateVehicle.id})`
    : '';
  
  useEffect(() => {
    if (isEditMode && currentVehicle) {
      // Sicherstellen, dass alle Felder korrekt initialisiert werden
      setFormData({
        vehicleNumber: currentVehicle.vehicleNumber || '',
        licensePlate: currentVehicle.licensePlate || '',
        model: currentVehicle.model || '',
        phoneNumber: currentVehicle.phoneNumber || '',
        ordnungsnummer: currentVehicle.ordnungsnummer || '',
        vehicleType: currentVehicle.vehicleType || 1,
        specialCapabilities: currentVehicle.specialCapabilities || migrateSpecialVehicleType(currentVehicle),
        taximeterCoupling: currentVehicle.taximeterCoupling || 'none',
        paymentMethods: currentVehicle.paymentMethods || 1,
        status: currentVehicle.status || 'available',
        maintenanceDue: Object.fromEntries(
          DUE_MAINTENANCE_TYPES.map(type => [type.key, getVehicleDueDate(currentVehicle, type.key)])
        ),
        documents: Array.isArray(currentVehicle.documents) ? currentVehicle.documents : []
      });
      
      // Debug-Information zum besseren Verständnis des Datenformats
      console.log('Geladene Fahrzeugdaten:', currentVehicle);
    }
  }, [currentVehicle, isEditMode]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };

    setFormData(updatedData);
    
    // Fehler zurücksetzen, wenn der Benutzer ein Feld ändert
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const handleDueDateChange = (key) => (date) => {
    setFormData(prev => ({
      ...prev,
      maintenanceDue: { ...(prev.maintenanceDue || {}), [key]: date }
    }));
  };
  
  // Dokumente speichert EntityDocumentsSection direkt am Fahrzeug; hier nur
  // die Anzeige nachziehen.
  const handleDocumentsChange = (documents) => {
    setFormData(prev => ({ ...prev, documents }));
  };
  
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    
    // Kennzeichen validieren: Pflichtfeld, deutsches Format, eindeutig.
    if (!formData.licensePlate) {
      newErrors.licensePlate = 'Kennzeichen ist erforderlich';
      valid = false;
    } else if (!isValidGermanPlate(formData.licensePlate)) {
      newErrors.licensePlate = 'Unvollständiges Kennzeichen (z. B. PB TI 727)';
      valid = false;
    } else if (duplicatePlateVehicle) {
      newErrors.licensePlate = duplicatePlateMessage;
      valid = false;
    }
    
    // Modell validieren
    if (!formData.model) {
      newErrors.model = 'Modell ist erforderlich';
      valid = false;
    }
    
    // Fahrzeugtyp validieren
    if (!formData.vehicleType || ![1, 2, 3, 4].includes(Number(formData.vehicleType))) {
      newErrors.vehicleType = 'Wählen Sie einen gültigen Fahrzeugtyp';
      valid = false;
    }
    
    // Zahlungsmethoden validieren
    if (!formData.paymentMethods || ![1, 2, 3].includes(Number(formData.paymentMethods))) {
      newErrors.paymentMethods = 'Wählen Sie eine gültige Zahlungsmethode';
      valid = false;
    }
    
    // Datumsfelder müssen nicht validiert werden, da sie null sein dürfen
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Kapazität basierend auf Fahrzeugtyp setzen - wird in der DB automatisch gesetzt
      const vehicleType = parseInt(formData.vehicleType);
      
      const vehicleData = {
        vehicleNumber: formData.vehicleNumber.trim(),
        licensePlate: formData.licensePlate,
        model: formData.model,
        phoneNumber: (formData.phoneNumber || '').trim(),
        // Ordnungsnummer bewusst ohne Duplikatprüfung — das Kennzeichen ist
        // bereits eindeutig (siehe duplicatePlateVehicle).
        ordnungsnummer: (formData.ordnungsnummer || '').trim(),
        vehicleType: vehicleType,
        specialCapabilities: formData.specialCapabilities || [],
        taximeterCoupling: formData.taximeterCoupling || 'none',
        paymentMethods: parseInt(formData.paymentMethods),
        status: formData.status,
        maintenanceDue: Object.fromEntries(
          Object.entries(formData.maintenanceDue || {})
            .filter(([, date]) => date && !Number.isNaN(date.getTime()))
            .map(([key, date]) => [key, date.toISOString()])
        )
      };
      if (!isEditMode && effectiveReserve) {
        vehicleData.active = false;
      }
      
      if (isEditMode) {
        // Update existing vehicle
        dispatch(updateVehicle({
          id,
          ...vehicleData
        })).then((result) => {
          if (!result.error) {
            navigate(fromPath || `/fahrzeuge/${id}`);
          }
        });
      } else {
        // Create new vehicle
        dispatch(createVehicle(vehicleData)).then(async (result) => {
          if (result.error) return;
          const newId = result.payload?.id;
          if (newId && pendingDocumentFiles.length > 0) {
            setSavingDocuments(true);
            const failed = await uploadPendingEntityDocuments('vehicle', newId, pendingDocumentFiles);
            setSavingDocuments(false);
            if (failed.length > 0) {
              navigate(`/fahrzeuge/${newId}/bearbeiten`, {
                state: {
                  documentUploadError: `Das Fahrzeug wurde angelegt, aber diese Dokumente konnten nicht hochgeladen werden: ${failed.join(', ')}. Bitte erneut hochladen.`
                }
              });
              return;
            }
          }
          navigate('/fahrzeuge');
        });
      }
    }
  };
  
  const handleCancel = () => {
    if (isEditMode) {
      navigate(fromPath || `/fahrzeuge/${id}`);
    } else {
      navigate('/fahrzeuge');
    }
  };
  
  const isLoading = vehicleLoading;
  
  if (isLoading && isEditMode) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  if (vehicleError) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daten"
        message={vehicleError}
        onRetry={() => isEditMode && dispatch(fetchVehicleById(id))}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={isEditMode ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug anlegen'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrzeuge', path: '/fahrzeuge' },
          { label: isEditMode ? 'Bearbeiten' : 'Neu', path: isEditMode ? `/fahrzeuge/${id}/bearbeiten` : '/fahrzeuge/neu' }
        ]}
      />
      
      <form onSubmit={handleSubmit}>
        {/* Pflichtfelder */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Pflichtfelder
          </Typography>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Die Anzahl der Sitzplätze wird automatisch anhand des Fahrzeugtyps bestimmt.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                id="vehicleNumber"
                name="vehicleNumber"
                label="Fahrzeugnummer"
                value={formData.vehicleNumber}
                onChange={handleChange}
                error={!!formErrors.vehicleNumber}
                helperText={formErrors.vehicleNumber || "Optional, z.B. 23 oder 023"}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                id="model"
                name="model"
                label="Fahrzeug Modell"
                value={formData.model}
                onChange={handleChange}
                error={!!formErrors.model}
                helperText={formErrors.model || "z.B. VW Touran, Mercedes E-Klasse"}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Kennzeichen *
              </Typography>
              <LicensePlateInput
                value={formData.licensePlate}
                onChange={(plate) => {
                  setFormData(prev => ({ ...prev, licensePlate: plate }));
                  if (formErrors.licensePlate) {
                    setFormErrors(prev => ({ ...prev, licensePlate: '' }));
                  }
                }}
                error={!!formErrors.licensePlate || !!duplicatePlateVehicle}
                helperText={
                  formErrors.licensePlate ||
                  duplicatePlateMessage ||
                  'Nur Großbuchstaben — Felder springen automatisch weiter'
                }
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Ordnungsnummer
              </Typography>
              <TextField
                fullWidth
                id="ordnungsnummer"
                name="ordnungsnummer"
                value={formData.ordnungsnummer}
                onChange={handleChange}
                inputProps={{ maxLength: 40 }}
                placeholder="z. B. 126"
                helperText="Von der Genehmigungsbehörde vergeben (Schild an der Heckscheibe). Steht auf der Quittung statt des Kennzeichens (optional)."
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                id="phoneNumber"
                name="phoneNumber"
                label="Fahrzeug-Telefonnummer"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="z.B. +49 170 1234567"
                helperText="Festes Smartphone im Fahrzeug (optional). Wird dem Kunden bevorzugt zum Anrufen angeboten."
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel id="vehicleType-label">Fahrzeugtyp</InputLabel>
                <Select
                  labelId="vehicleType-label"
                  id="vehicleType"
                  name="vehicleType"
                  value={formData.vehicleType}
                  onChange={handleChange}
                  label="Fahrzeugtyp"
                  error={!!formErrors.vehicleType}
                >
                  {VEHICLE_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                {formErrors.vehicleType && (
                  <FormHelperText error>{formErrors.vehicleType}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="specialCapabilities-label">Spezialfähigkeiten</InputLabel>
                <Select
                  labelId="specialCapabilities-label"
                  id="specialCapabilities"
                  multiple
                  value={formData.specialCapabilities}
                  onChange={(e) => setFormData({ ...formData, specialCapabilities: e.target.value })}
                  label="Spezialfähigkeiten"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map(s => (
                        <Chip key={s} label={SPECIAL_CAPABILITIES[s] || s} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {/* Checkboxen machen sichtbar, dass Mehrfachauswahl möglich
                      ist — das Menü bleibt beim Klicken offen, Haken zeigen
                      den Zustand. */}
                  {Object.entries(SPECIAL_CAPABILITIES).map(([key, label]) => (
                    <MenuItem key={key} value={key} dense>
                      <Checkbox
                        checked={formData.specialCapabilities.includes(key)}
                        size="small"
                        sx={{ py: 0, mr: 1 }}
                      />
                      <ListItemText primary={label} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Optional — Mehrfachauswahl möglich</FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel id="paymentMethods-label">Zahlungsmöglichkeiten</InputLabel>
                <Select
                  labelId="paymentMethods-label"
                  id="paymentMethods"
                  name="paymentMethods"
                  value={formData.paymentMethods}
                  onChange={handleChange}
                  label="Zahlungsmöglichkeiten"
                  error={!!formErrors.paymentMethods}
                >
                  <MenuItem value={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                      Nur Barzahlung
                    </Box>
                  </MenuItem>
                  <MenuItem value={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                      <CreditCardIcon color="primary" sx={{ mr: 1 }} />
                      Bar- und Kartenzahlung
                    </Box>
                  </MenuItem>
                  <MenuItem value={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                      <CreditCardIcon color="primary" sx={{ mr: 1 }} />
                      <PayPalIcon color="primary" sx={{ mr: 1 }} />
                      Bar-, Karten- und PayPal-Zahlung
                    </Box>
                  </MenuItem>
                </Select>
                {formErrors.paymentMethods && (
                  <FormHelperText error>{formErrors.paymentMethods}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {/* Steuert, ob der Fahrer beim Abschluss einer Krankenfahrt nach
                  dem geeichten Zaehlerwert gefragt wird. Nur wirksam, wenn in
                  den Einstellungen der Modus „Taxameterwert erfassen" aktiv
                  ist — gemischte Flotten sind der Normalfall.
                  krankenfahrten_tarife_konzept.md §6.1 */}
              <FormControl fullWidth>
                <InputLabel id="taximeterCoupling-label">Taxameter-Kopplung</InputLabel>
                <Select
                  labelId="taximeterCoupling-label"
                  id="taximeterCoupling"
                  name="taximeterCoupling"
                  value={formData.taximeterCoupling}
                  onChange={handleChange}
                  label="Taxameter-Kopplung"
                >
                  <MenuItem value="none">Kein Taxameter-Abgleich</MenuItem>
                  <MenuItem value="seat_contact">Taxameter mit Sitzkontakt</MenuItem>
                  <MenuItem value="manual">Taxameter, manuell bedient</MenuItem>
                </Select>
                <FormHelperText>
                  Bei Krankenfahrten fragt die Fahrer-App dann nach dem geeichten
                  Zählerwert. Dieser ist ein Beleg, kein Kassenbetrag.
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* Status-Feld ausgeblendet, da standardmäßig auf "verfügbar" gesetzt */}
          </Grid>
        </FormPaper>
        
        {/* Optionale Felder */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Fristen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Nächste Fälligkeiten. Ab 30 Tagen vorher erscheint ein Hinweis in der Fahrzeugliste,
            dazu eine Benachrichtigung im Portal. Beim Eintragen einer Wartung auf der
            Fahrzeugseite wird die Fälligkeit automatisch fortgeschrieben.
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <Grid container spacing={3}>
              {DUE_MAINTENANCE_TYPES.map(type => (
                <Grid key={type.key} size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label={`${type.label} fällig am`}
                    value={formData.maintenanceDue?.[type.key] || null}
                    onChange={handleDueDateChange(type.key)}
                    slotProps={{
                      textField: { fullWidth: true },
                      field: { clearable: true }
                    }}
                    format="dd.MM.yyyy"
                  />
                </Grid>
              ))}
            </Grid>
          </LocalizationProvider>
        </FormPaper>
        
        {/* Dokumente */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Fahrzeugdokumente
          </Typography>
          
          {documentUploadError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {documentUploadError}
            </Alert>
          )}
          <EntityDocumentsSection
            kind="vehicle"
            entityId={isEditMode ? id : null}
            documents={formData.documents}
            onDocumentsChange={handleDocumentsChange}
            pendingFiles={pendingDocumentFiles}
            onPendingFilesChange={setPendingDocumentFiles}
          />
        </FormPaper>
        
        {!isEditMode && (
          <FormPaper>
            <FormControlLabel
              control={
                <Switch
                  checked={effectiveReserve}
                  disabled={reserveForced}
                  onChange={(e) => setCreateAsReserve(e.target.checked)}
                />
              }
              label="Als Reservefahrzeug anlegen (deaktiviert)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Reservefahrzeuge belegen keinen Platz im Fahrzeugkontingent und sind in der
              Fahrer-App nicht wählbar. Aktivieren Sie das Fahrzeug später in der Fahrzeugliste,
              z. B. wenn ein anderes Auto ausfällt und dafür deaktiviert wird.
            </Typography>
            {reserveForced && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Das Fahrzeugkontingent ist ausgeschöpft ({quotaStatus.vehicleCount}/{quotaStatus.licenseInfo.limit} aktiv).
                Neue Fahrzeuge lassen sich deshalb nur als Reserve anlegen.
              </Alert>
            )}
          </FormPaper>
        )}

        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isLoading || savingDocuments}
          >
            {savingDocuments ? 'Dokumente werden hochgeladen …' : isEditMode ? 'Änderungen speichern' : effectiveReserve ? 'Reservefahrzeug erstellen' : 'Fahrzeug erstellen'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default VehicleForm;
